/**
 * Portal action pages — Can I claim it? · Can I afford it? · Ask accountant.
 *
 * Can I claim it? is a full flow: upload receipt → describe → business % →
 * server calls Claude Sonnet 4.5 (vision) → returns verdict + tax saving
 * → borderline/no verdicts auto-escalate a message thread with the firm.
 * See /app/backend/routes_claim_check.py for the full backend contract.
 *
 * Can I afford it? and Ask your accountant remain lightweight stubs for
 * the next sprint.
 */
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, FileCheck2, LineChart, MessageCircle, Sparkles, Upload as UploadIcon, Camera,
    Loader2, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Send, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

/* ---------------- Shared: Send-to-accountant panel with optional note ------
 * Two-step reveal: first tap opens the note input, second tap sends. Client
 * can skip the note by just tapping Send. Idempotent — backend returns
 * `already_sent: true` on repeat clicks. */
function SendToAccountantPanel({
    onSend, sent, threadId, onOpenThread,
    variant = "primary",   // "primary" (violet solid) | "secondary" (subtle outline)
    primaryLabel = "Send this to my accountant",
    secondaryLabel = "Send to my accountant anyway",
    testId = "send-to-accountant",
}) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    const [sending, setSending] = useState(false);

    async function submit() {
        setSending(true);
        try {
            await onSend(note.trim() || null);
        } finally {
            setSending(false);
        }
    }

    if (sent) {
        return (
            <div className="rounded-xl bg-violet-50 border border-violet-200 p-4 flex items-start gap-3" data-testid={`${testId}-sent`}>
                <div className="grid h-9 w-9 place-items-center rounded-full bg-violet-600 text-white shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-violet-900">Sent to your accountant</div>
                    <p className="mt-0.5 text-[12.5px] text-violet-800 leading-relaxed">
                        Your accountant has everything they need. Expect a reply in your messages inbox.
                    </p>
                    {threadId && (
                        <button
                            type="button"
                            onClick={onOpenThread}
                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-violet-900 hover:underline"
                            data-testid={`${testId}-open-thread`}
                        >
                            Open the thread <MessageCircle className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    // Collapsed button — first tap opens the note editor.
    if (!open) {
        const cls = variant === "primary"
            ? "bg-violet-600 text-white hover:bg-violet-700 font-medium"
            : "border border-violet-300 bg-white text-violet-700 hover:bg-violet-50 text-sm font-medium";
        const label = variant === "primary" ? primaryLabel : secondaryLabel;
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 disabled:opacity-60 ${cls}`}
                data-testid={testId}
            >
                <Send className="h-4 w-4" /> {label}
            </button>
        );
    }

    // Expanded note editor.
    return (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-3" data-testid={`${testId}-editor`}>
            <div>
                <label className="text-[10px] uppercase tracking-[0.14em] text-violet-900" htmlFor={`${testId}-note`}>
                    Anything you&apos;d like to add?
                </label>
                <textarea
                    id={`${testId}-note`}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Optional — e.g. 'Not sure if I logged the km properly' or 'This is for a specific customer'"
                    className="mt-1.5 w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none"
                    data-testid={`${testId}-note-input`}
                    autoFocus
                />
                <div className="mt-1 flex items-center justify-between text-[10px] text-violet-800">
                    <span>Skip if there&apos;s nothing to add — we&apos;ll still attach all the context.</span>
                    <span className="tabular-nums">{note.length}/500</span>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => { setOpen(false); setNote(""); }}
                    disabled={sending}
                    className="flex-1 rounded-lg border border-violet-200 bg-white py-2.5 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-60"
                    data-testid={`${testId}-cancel`}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={sending}
                    className="flex-1 rounded-lg bg-violet-600 text-white py-2.5 text-sm font-medium hover:bg-violet-700 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                    data-testid={`${testId}-confirm`}
                >
                    {sending ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending…</> : <><Send className="h-3.5 w-3.5" /> Send{note.trim() ? " with note" : ""}</>}
                </button>
            </div>
        </div>
    );
}

function Shell({ back, title, subtitle, gradient, icon: Icon, children, testId }) {
    return (
        <div className="space-y-5" data-testid={testId}>
            <Link to={back} className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
            <div className={"relative rounded-2xl overflow-hidden p-6 bg-gradient-to-br text-white " + gradient}>
                <div className="flex items-start gap-4">
                    <div className="grid h-14 w-14 place-items-center rounded-full bg-white/95 text-[#0F1115]">
                        <Icon className="h-6 w-6" strokeWidth={2.2} />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-semibold tracking-tight">{title}</h1>
                        <p className="mt-1 text-sm text-white/90 max-w-md">{subtitle}</p>
                    </div>
                </div>
            </div>
            {children}
        </div>
    );
}

/* ================== CAN I CLAIM IT? ================== */

export function PortalClaim() {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [description, setDescription] = useState("");
    const [purpose, setPurpose] = useState("");
    const [amount, setAmount] = useState("");
    const [businessPct, setBusinessPct] = useState(100);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);

    function onFile(f) {
        if (!f) return;
        const isImage = /^image\/(jpeg|png|webp|gif|heic|heif)$/i.test(f.type);
        const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
        if (!isImage && !isPdf) {
            toast.error("Attach an image (JPG/PNG/WEBP) or a PDF");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("File is too big (8 MB max)");
            return;
        }
        setFile(f);
        if (isPdf) {
            setPreview({ kind: "pdf", name: f.name || "receipt.pdf", size: f.size });
        } else {
            const reader = new FileReader();
            reader.onload = (e) => setPreview({ kind: "image", url: e.target.result });
            reader.readAsDataURL(f);
        }
    }

    async function submit() {
        if (!description.trim()) { toast.error("Tell us what you bought"); return; }
        if (!purpose.trim()) { toast.error("Tell us what it's for"); return; }
        const amt = parseFloat(amount);
        if (!(amt > 0)) { toast.error("Enter a valid amount"); return; }

        setBusy(true);
        try {
            const fd = new FormData();
            if (file) fd.append("image", file);
            fd.append("description", description);
            fd.append("purpose", purpose);
            fd.append("amount", String(amt));
            fd.append("business_pct", String(businessPct));
            const { data } = await api.post("/claim-check", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function reset() {
        setFile(null); setPreview(null); setDescription(""); setPurpose("");
        setAmount(""); setBusinessPct(100); setResult(null);
    }

    return (
        <Shell
            back="/portal"
            testId="portal-claim-page"
            title="Can I claim it?"
            subtitle="Describe the purchase (attach a receipt if you have one) and we'll check if it's tax-deductible."
            gradient="from-emerald-500 to-emerald-700"
            icon={FileCheck2}
        >
            {result ? (
                <ResultCard result={result} onAgain={reset} />
            ) : (
                <div className="rounded-xl border border-[#E4E4E7] bg-white p-5 space-y-5" data-testid="claim-form">
                    {/* Attachment (optional) */}
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Receipt or invoice <span className="normal-case tracking-normal text-[#9AA0A6]">(optional)</span></label>
                            {preview && (
                                <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="text-[11px] text-emerald-700 hover:underline" data-testid="claim-clear-image">
                                    Remove
                                </button>
                            )}
                        </div>
                        {preview?.kind === "image" ? (
                            <div className="mt-2 relative">
                                <img src={preview.url} alt="Receipt preview" className="w-full max-h-64 object-contain rounded-lg border border-[#E4E4E7] bg-[#FAFAFA]" />
                            </div>
                        ) : preview?.kind === "pdf" ? (
                            <div className="mt-2 flex items-center gap-3 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="claim-pdf-preview">
                                <div className="grid h-10 w-10 place-items-center rounded-lg bg-rose-100 text-rose-700 text-[10px] font-bold">PDF</div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{preview.name}</div>
                                    <div className="text-[11px] text-[#5E636E]">{(preview.size / 1024).toFixed(0)} KB · PDF attached</div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button type="button" onClick={() => cameraRef.current?.click()} className="rounded-lg border-2 border-dashed border-[#E4E4E7] p-5 flex flex-col items-center gap-1.5 text-[#5E636E] hover:border-emerald-500 hover:text-emerald-700 transition-colors" data-testid="claim-take-photo">
                                        <Camera className="h-6 w-6" />
                                        <span className="text-xs font-medium">Take photo</span>
                                    </button>
                                    <button type="button" onClick={() => fileRef.current?.click()} className="rounded-lg border-2 border-dashed border-[#E4E4E7] p-5 flex flex-col items-center gap-1.5 text-[#5E636E] hover:border-emerald-500 hover:text-emerald-700 transition-colors" data-testid="claim-pick-file">
                                        <UploadIcon className="h-6 w-6" />
                                        <span className="text-xs font-medium">Upload image or PDF</span>
                                    </button>
                                    <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                                    <input ref={fileRef} type="file" accept="image/*,application/pdf,.pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                                </div>
                                <p className="mt-2 text-[11px] text-[#5E636E]">
                                    Screenshots, photos and PDFs work. Or skip the attachment and we&apos;ll rely on your description.
                                </p>
                            </>
                        )}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="claim-desc">What did you buy?</label>
                        <input
                            id="claim-desc"
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g. Power drill for the workshop"
                            className="mt-1.5 w-full rounded-lg border border-[#E4E4E7] px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            data-testid="claim-input-description"
                        />
                    </div>

                    {/* Purpose */}
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="claim-purpose">What&apos;s it for?</label>
                        <input
                            id="claim-purpose"
                            type="text"
                            value={purpose}
                            onChange={(e) => setPurpose(e.target.value)}
                            placeholder="e.g. Fitting kitchen benches for a customer's build"
                            className="mt-1.5 w-full rounded-lg border border-[#E4E4E7] px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                            data-testid="claim-input-purpose"
                        />
                    </div>

                    {/* Amount + Business % */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="claim-amount">Amount paid (NZD, incl. GST)</label>
                            <div className="mt-1.5 relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5E636E]">$</span>
                                <input
                                    id="claim-amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full rounded-lg border border-[#E4E4E7] pl-6 pr-3 py-2 text-sm tabular-nums focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                                    data-testid="claim-input-amount"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="claim-bizpct">Business use</label>
                            <div className="mt-1.5">
                                <input
                                    id="claim-bizpct"
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="5"
                                    value={businessPct}
                                    onChange={(e) => setBusinessPct(parseInt(e.target.value, 10))}
                                    className="w-full accent-emerald-600"
                                    data-testid="claim-input-businesspct"
                                />
                                <div className="text-center text-sm font-medium mt-0.5 tabular-nums">{businessPct}%</div>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white py-3 font-medium hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="claim-submit"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Checking against IRD rules… this can take 10-15 seconds
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-4 w-4" />
                                Check it
                            </>
                        )}
                    </button>

                    <div className="pt-2 border-t border-[#E4E4E7] text-[11px] text-[#5E636E] leading-relaxed">
                        We check IRD deductibility for your entity type, and your accountant sees every review. Anything borderline is auto-flagged to your accountant so you don&apos;t have to chase.
                    </div>
                </div>
            )}
        </Shell>
    );
}

function ResultCard({ result, onAgain }) {
    const nav = useNavigate();
    const [sent, setSent] = useState(!!result.escalated);
    const [threadId, setThreadId] = useState(result.escalation_thread_id || null);

    const v = result.verdict;
    const tone =
        v === "yes" ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: CheckCircle2, label: "Claimable" }
            : v === "partial" ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle, label: "Partially claimable" }
                : v === "borderline" ? { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-900", icon: HelpCircle, label: "Needs a human eye" }
                    : { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", icon: XCircle, label: "Not claimable" };
    const Icon = tone.icon;
    const canClaim = v === "yes" || v === "partial";
    const suggestEscalate = !!result.should_escalate;

    async function sendToAccountant(note) {
        try {
            const { data } = await api.post(`/claim-check/${result.id}/escalate`, { note: note || null });
            setSent(true);
            setThreadId(data.escalation_thread_id);
            toast.success(data.already_sent ? "Already sent — opening the thread" : "Sent to your accountant");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="space-y-4" data-testid="claim-result">
            {/* Verdict banner */}
            <div className={`rounded-xl border p-4 ${tone.bg} ${tone.border}`}>
                <div className="flex items-start gap-3">
                    <div className={`grid h-10 w-10 place-items-center rounded-full ${tone.text} bg-white/70`}>
                        <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className={`text-[10px] uppercase tracking-[0.14em] ${tone.text} opacity-70`}>Verdict</div>
                        <div className={`text-lg font-semibold ${tone.text}`} data-testid="claim-result-verdict">{tone.label}</div>
                        <p className={`mt-1.5 text-[13px] leading-relaxed ${tone.text} opacity-90`}>{result.explanation}</p>
                    </div>
                </div>
            </div>

            {/* Money row (only when there IS a deductible amount) */}
            {canClaim && (
                <div className="rounded-xl border border-[#E4E4E7] bg-white p-4 grid grid-cols-2 gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Deductible amount</div>
                        <div className="mt-1 font-display text-2xl tabular-nums text-emerald-700">{NZD.format(result.deductible_amount)}</div>
                        <div className="mt-0.5 text-[11px] text-[#5E636E]">{result.business_pct}% business use</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Estimated tax saving</div>
                        <div className="mt-1 font-display text-2xl tabular-nums text-emerald-700" data-testid="claim-result-saving">{NZD.format(result.tax_saving)}</div>
                        <div className="mt-0.5 text-[11px] text-[#5E636E]">at your {(result.marginal_rate * 100).toFixed(0)}% rate</div>
                    </div>
                </div>
            )}

            {/* Category + confidence + red flags */}
            <div className="rounded-xl border border-[#E4E4E7] bg-white p-4">
                <div className="flex items-center justify-between text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">Category</div>
                        <div className="mt-0.5 font-medium">{result.category}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">AI confidence</div>
                        <div className="mt-0.5 font-medium tabular-nums">{Math.round(result.confidence * 100)}%</div>
                    </div>
                </div>
                {result.red_flags?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E] mb-1.5">Things to check</div>
                        <ul className="space-y-1 text-[12px] text-[#0F1115]">
                            {result.red_flags.map((f, i) => (<li key={i}>• {f}</li>))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Send to accountant — MANUAL only, with optional note. */}
            <SendToAccountantPanel
                onSend={sendToAccountant}
                sent={sent}
                threadId={threadId}
                onOpenThread={() => nav("/portal/messages")}
                variant={suggestEscalate ? "primary" : "secondary"}
                testId="claim-send"
            />

            <button
                type="button"
                onClick={onAgain}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#E4E4E7] py-3 text-sm font-medium hover:bg-[#F5F4EF]"
                data-testid="claim-check-another"
            >
                <RotateCcw className="h-4 w-4" /> Check another
            </button>
        </div>
    );
}

/* ================== CAN I AFFORD IT? ================== */

export function PortalAfford() {
    const [amount, setAmount] = useState("");
    const [description, setDescription] = useState("");
    const [isBiz, setIsBiz] = useState(false);
    const [businessPct, setBusinessPct] = useState(100);
    const [deferTax, setDeferTax] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    async function submit() {
        const amt = parseFloat(amount);
        if (!(amt > 0)) { toast.error("Enter a valid amount"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/afford-check", {
                amount: amt,
                description: description || null,
                is_business_purchase: isBiz,
                business_pct: businessPct,
                defer_tax: deferTax,
            });
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function reset() {
        setAmount(""); setDescription(""); setIsBiz(false);
        setBusinessPct(100); setDeferTax(false); setResult(null);
    }

    return (
        <Shell
            back="/portal"
            testId="portal-afford-page"
            title="Can I afford it?"
            subtitle="Punch in an amount. We'll show you exactly what it does to your cash, tax reserve and runway — before you commit."
            gradient="from-sky-500 to-blue-700"
            icon={LineChart}
        >
            {result ? (
                <AffordResult result={result} onAgain={reset} />
            ) : (
                <div className="rounded-xl border border-[#E4E4E7] bg-white p-5 space-y-5" data-testid="afford-form">
                    {/* Amount */}
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="afford-amount">How much are you thinking of spending?</label>
                        <div className="mt-1.5 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-[#5E636E]">$</span>
                            <input
                                id="afford-amount"
                                type="number"
                                step="0.01"
                                min="0"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="w-full rounded-lg border border-[#E4E4E7] pl-7 pr-3 py-3 text-lg font-medium tabular-nums focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                                data-testid="afford-input-amount"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Description (optional) */}
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="afford-desc">What is it? <span className="normal-case text-[#B0B4BB]">(optional)</span></label>
                        <input
                            id="afford-desc"
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g. Work van, new machinery, hiring a part-timer"
                            className="mt-1.5 w-full rounded-lg border border-[#E4E4E7] px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                            data-testid="afford-input-description"
                        />
                    </div>

                    {/* Business purchase */}
                    <div className="rounded-lg border border-[#E4E4E7] p-3">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isBiz}
                                onChange={(e) => setIsBiz(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-sky-600"
                                data-testid="afford-input-isbiz"
                            />
                            <div>
                                <div className="text-sm font-medium">It&apos;s a business purchase</div>
                                <p className="text-[11px] text-[#5E636E] mt-0.5 leading-relaxed">
                                    We&apos;ll factor in the tax you&apos;ll save next quarter because it&apos;s deductible.
                                </p>
                            </div>
                        </label>
                        {isBiz && (
                            <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
                                <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="afford-bizpct">Business use %</label>
                                <input
                                    id="afford-bizpct"
                                    type="range"
                                    min="0" max="100" step="5"
                                    value={businessPct}
                                    onChange={(e) => setBusinessPct(parseInt(e.target.value, 10))}
                                    className="w-full accent-sky-600 mt-1"
                                    data-testid="afford-input-businesspct"
                                />
                                <div className="text-center text-sm font-medium tabular-nums">{businessPct}%</div>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 text-white py-3 font-medium hover:bg-sky-700 disabled:opacity-60"
                        data-testid="afford-submit"
                    >
                        {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Running the numbers…</>) : (<><LineChart className="h-4 w-4" /> Check impact</>)}
                    </button>

                    <div className="pt-2 border-t border-[#E4E4E7] text-[11px] text-[#5E636E] leading-relaxed">
                        All figures are pulled straight from your books — no guessing. Nothing here is committed until you actually make the purchase.
                    </div>
                </div>
            )}
        </Shell>
    );
}

function AffordResult({ result, onAgain }) {
    const v = result.verdict;
    const tone =
        v === "green" ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: CheckCircle2 }
            : v === "amber" ? { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", icon: AlertTriangle }
                : { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", icon: XCircle };
    const Icon = tone.icon;
    const b = result.before || {};
    const a = result.after || {};

    return (
        <div className="space-y-4" data-testid="afford-result">
            {/* Verdict */}
            <div className={`rounded-xl border p-4 ${tone.bg} ${tone.border}`}>
                <div className="flex items-start gap-3">
                    <div className={`grid h-10 w-10 place-items-center rounded-full ${tone.text} bg-white/70`}>
                        <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className={`text-[10px] uppercase tracking-[0.14em] ${tone.text} opacity-70`}>Verdict</div>
                        <div className={`text-lg font-semibold ${tone.text}`} data-testid="afford-result-verdict">{result.verdict_label}</div>
                        <ul className={`mt-2 space-y-1 text-[13px] leading-relaxed ${tone.text} opacity-90`}>
                            {(result.reasons || []).map((r, i) => (<li key={i}>• {r}</li>))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Before / after grid */}
            <div className="rounded-xl border border-[#E4E4E7] bg-white overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-[#E4E4E7]">
                    <StatCol label="Before" cash={b.cash_balance} safe={b.safe_to_spend} runway={b.runway_weeks} />
                    <StatCol label="After" cash={a.cash_balance} safe={a.safe_to_spend} runway={a.runway_weeks} highlight />
                </div>
                <div className="border-t border-[#E4E4E7] px-4 py-2.5 flex items-center justify-between text-[11px] text-[#5E636E]">
                    <span>Purchase: {NZD.format(result.purchase.amount)}{result.purchase.description ? ` · ${result.purchase.description}` : ""}</span>
                    <span className="tabular-nums">~{NZD.format(result.weekly_outflow)}/wk outflow</span>
                </div>
            </div>

            {/* Suggestions */}
            {result.suggestions?.length > 0 && (
                <div className="rounded-xl border border-[#E4E4E7] bg-white p-4">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E] mb-1.5">Suggestions</div>
                    <ul className="space-y-1.5 text-[13px] text-[#0F1115] leading-relaxed">
                        {result.suggestions.map((s, i) => (<li key={i}>• {s}</li>))}
                    </ul>
                </div>
            )}

            <button
                type="button"
                onClick={onAgain}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#E4E4E7] py-3 text-sm font-medium hover:bg-[#F5F4EF]"
                data-testid="afford-check-another"
            >
                <RotateCcw className="h-4 w-4" /> Try a different amount
            </button>
        </div>
    );
}

function StatCol({ label, cash, safe, runway, highlight }) {
    const neg = (v) => v != null && v < 0;
    return (
        <div className={"p-4 " + (highlight ? "bg-[#F5F8FA]" : "")}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <dl className="mt-2 space-y-2 text-sm">
                <div>
                    <dt className="text-[11px] text-[#5E636E]">Cash on hand</dt>
                    <dd className={"font-display text-lg tabular-nums " + (neg(cash) ? "text-rose-600" : "text-[#0F1115]")}>
                        {cash == null ? "—" : NZD.format(cash)}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] text-[#5E636E]">Safe to spend</dt>
                    <dd className={"font-display text-lg tabular-nums " + (neg(safe) ? "text-amber-700" : "text-emerald-700")}>
                        {safe == null ? "—" : NZD.format(safe)}
                    </dd>
                </div>
                <div>
                    <dt className="text-[11px] text-[#5E636E]">Runway</dt>
                    <dd className={"font-display text-lg tabular-nums " + (runway != null && runway < 4 ? "text-rose-600" : runway != null && runway < 8 ? "text-amber-700" : "text-[#0F1115]")}>
                        {runway == null ? "—" : `${runway} wk`}
                    </dd>
                </div>
            </dl>
        </div>
    );
}

/* ================== ASK YOUR ACCOUNTANT ================== */

const ASK_SUGGESTIONS = [
    "Why is my safe-to-spend so low?",
    "Am I on track for my next tax bill?",
    "How much runway do I have?",
    "What's my biggest expense category?",
];

export function PortalAsk() {
    const nav = useNavigate();
    const [question, setQuestion] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const inputRef = useRef(null);

    async function submit(force = false) {
        if (!force) {
            if (!question.trim() || question.trim().length < 5) {
                toast.error("Please type a question first");
                return;
            }
        }
        setBusy(true);
        try {
            const { data } = await api.post("/ask-accountant", {
                question: question.trim(),
                force_escalate: !!force,
            });
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function reset() {
        setQuestion(""); setResult(null);
        setTimeout(() => inputRef.current?.focus(), 50);
    }

    return (
        <Shell
            back="/portal"
            testId="portal-ask-page"
            title="Ask your accountant"
            subtitle="Type your question in plain English. The AI checks your own numbers first — anything tricky goes straight to your accountant with all the context attached."
            gradient="from-violet-500 to-purple-700"
            icon={MessageCircle}
        >
            {result ? (
                <AskResult
                    result={result}
                    question={question}
                    onAgain={reset}
                    onOpenThread={() => nav("/portal/messages")}
                />
            ) : (
                <div className="rounded-xl border border-[#E4E4E7] bg-white p-5 space-y-4" data-testid="ask-form">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]" htmlFor="ask-input">Your question</label>
                        <textarea
                            ref={inputRef}
                            id="ask-input"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="e.g. Am I on track for my next tax bill? Or should I be worried?"
                            rows={4}
                            maxLength={500}
                            className="mt-1.5 w-full rounded-lg border border-[#E4E4E7] px-3 py-2.5 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none"
                            data-testid="ask-input"
                            autoFocus
                        />
                        <div className="mt-1 text-right text-[10px] text-[#B0B4BB] tabular-nums">{question.length}/500</div>
                    </div>

                    <div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E] mb-2">Or try one of these</div>
                        <div className="flex flex-wrap gap-2">
                            {ASK_SUGGESTIONS.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setQuestion(s)}
                                    className="text-[11.5px] rounded-full border border-[#E4E4E7] bg-white px-3 py-1.5 hover:border-violet-500 hover:text-violet-700 transition-colors"
                                    data-testid={`ask-suggestion-${s.slice(0, 12).toLowerCase().replaceAll(" ", "-")}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => submit(false)}
                        disabled={busy}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 text-white py-3 font-medium hover:bg-violet-700 disabled:opacity-60"
                        data-testid="ask-submit"
                    >
                        {busy ? (<><Loader2 className="h-4 w-4 animate-spin" /> Checking your books…</>) : (<><Sparkles className="h-4 w-4" /> Get an answer</>)}
                    </button>

                    <div className="pt-2 border-t border-[#E4E4E7] text-[11px] text-[#5E636E] leading-relaxed">
                        The AI only uses your own numbers to answer. Anything about IRD rules, tax advice, or a judgement call is passed straight to your accountant — you never get made-up advice.
                    </div>
                </div>
            )}
        </Shell>
    );
}

function AskResult({ result, question, onAgain, onOpenThread }) {
    const [sent, setSent] = useState(!!result.escalated);
    const [threadId, setThreadId] = useState(result.escalation_thread_id || null);
    const answered = !!result.answer && !result.needs_human;
    const suggestEscalate = !answered;

    async function sendToAccountant(note) {
        try {
            const { data } = await api.post("/ask-accountant/escalate", {
                session_id: result.session_id,
                note: note || null,
            });
            setSent(true);
            setThreadId(data.escalation_thread_id);
            toast.success(data.already_sent ? "Already sent — opening the thread" : "Sent to your accountant");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="space-y-4" data-testid="ask-result">
            {/* Your question card */}
            <div className="rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#5E636E]">You asked</div>
                <p className="mt-1 text-[13px] text-[#0F1115] leading-relaxed">{question}</p>
            </div>

            {/* AI answer */}
            {answered && (
                <div className="rounded-xl border border-violet-200 bg-violet-50 p-4" data-testid="ask-answer">
                    <div className="flex items-start gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-violet-600 text-white shrink-0">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-[10px] uppercase tracking-[0.14em] text-violet-900 opacity-70">AI answer</div>
                                <div className="text-[10px] text-violet-800 tabular-nums">{Math.round(result.confidence * 100)}% confident</div>
                            </div>
                            <p className="mt-1 text-[13.5px] text-violet-900 leading-relaxed">{result.answer}</p>
                            {result.sources_used?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {result.sources_used.map((s) => (
                                        <span key={s} className="text-[10px] uppercase tracking-[0.08em] bg-white/60 text-violet-800 rounded-full px-2 py-0.5">{s}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* When AI couldn't answer, surface the reason inline */}
            {!answered && !sent && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="ask-needs-human">
                    <div className="flex items-start gap-3">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-sky-500 text-white shrink-0">
                            <HelpCircle className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-sky-900">This one needs a human eye</div>
                            <p className="mt-0.5 text-[12.5px] text-sky-800 leading-relaxed">
                                {result.escalation_reason || "The AI couldn't answer this confidently. Tap below to send it to your accountant with the full context attached."}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Sent confirmation OR the send panel */}
            <SendToAccountantPanel
                onSend={sendToAccountant}
                sent={sent}
                threadId={threadId}
                onOpenThread={onOpenThread}
                variant={suggestEscalate ? "primary" : "secondary"}
                testId="ask-send"
            />

            <button
                type="button"
                onClick={onAgain}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#E4E4E7] py-3 text-sm font-medium hover:bg-[#F5F4EF]"
                data-testid="ask-another"
            >
                <RotateCcw className="h-4 w-4" /> Ask something else
            </button>
        </div>
    );
}
